"use client";

import { readHasPriorInAppNav } from "@/app/components/shared/nav-tracker";
import { PayPalMessage } from "@pamperedchef/consumer-components/payment";
import { RecentlyViewedProducts } from "@/app/components/shared/recently-viewed-products";
import { RecommendedProductsBlock } from "@/app/components/shared/recommended-products";
import { ReviewsRatings } from "@/app/components/shared/reviews-ratings";
import { useAddToCartSlider } from "@/app/hooks/useAddToCartSlider";
import { useMediaQuery } from "@/app/hooks/useMediaQuery";
import { useStickyBarVisibility } from "@/app/hooks/useStickyBarVisibility";
import { useAddToCartErrorStore, useUserStore } from "@/app/store";
import { type LocaleState } from "@/app/store";
import { analyticsManager } from "@/app/utilities/analytics/analytics-manager";
import { type PdpContentClickAction } from "@/app/utilities/analytics/analytics-events";
import { useTrackView } from "@/app/utilities/analytics/use-track-view";
import { breakpoint } from "@/app/utilities/breakpoint";
import { type StorefrontUrls } from "@/app/utilities/storefront-urls";
import { StickyCartBar } from "@/composites/cart";
import { Breadcrumbs } from "@/composites/navigation";
import { ProductSummary } from "@/composites/product";
import {
	ProductTagList,
	selectProductTags
} from "@/composites/product/ProductTagList";
import { ProductGallery } from "@/composites/product/ProductGallery";
import { type Locale } from "@/domain";
import {
	type CatalogContentBlock,
	type CatalogProduct
} from "@/domain/catalog";
import {
	AlertBanner,
	Container,
	Col as DSCol,
	Grid,
	type ColProps
} from "@pamperedchef/design-system";
import { useLocale } from "next-intl";
import { usePathname } from "next/navigation";
import {
	useCallback,
	useEffect,
	useState,
	type FC,
	type ReactNode
} from "react";
import { buildUseAndCareHref } from "@/features/use-and-care/helpers/build-use-and-care-href";
import { resolveUseAndCareGuide } from "@/features/use-and-care/helpers/resolve-use-and-care";
import { AddToListRoot } from "./AddToList";
import { ConsultantBanner } from "./ConsultantBanner";
import { ContentBlocksRenderer } from "./ContentBlocksRenderer";
import type { AboutTheProductResolved } from "./blocks/AboutTheProduct";
import type { UpsellSetData } from "./blocks/UpsellSet";
import { buildBreadcrumbItems } from "./helpers/build-breadcrumb-items";
import { resolveConsultantSellingTips } from "./helpers/resolve-consultant-selling-tips";
import { type ProductDetailConfig } from "./product-detail-config";
import type { NormalizedRecipe } from "./helpers/recipe-pairings/normalized-recipe";
import { toGallerySlides } from "./helpers/to-gallery-slides";
import * as styles from "./ProductDetailTemplate.module.scss";

// Relative-href defaults for stories / tests that don't pass `urls`. The real
// storefront URLs are resolved server-side via `getStorefrontUrls()` in the
// route and threaded down — never resolved inside this client component.
const DEFAULT_URLS: StorefrontUrls = {
	home: "/",
	categoryBase: "/shop",
	useAndCareBase: "/shop/useandcare"
};

const Col = DSCol as FC<ColProps & { children?: ReactNode }>;

export interface ProductDetailTemplateProps {
	product: CatalogProduct;
	urls?: StorefrontUrls;
	resolvedRecipePairings?: NormalizedRecipe[][];
	resolvedUpsellSets?: UpsellSetData[];
	config?: ProductDetailConfig;
	lowStockLabel?: string;
}
export function ProductDetailTemplate({
	product,
	urls = DEFAULT_URLS,
	resolvedRecipePairings,
	resolvedUpsellSets,
	config,
	lowStockLabel
}: ProductDetailTemplateProps) {
	const locale = useLocale() as Locale;
	const [quantity, setQuantity] = useState(1);
	const showBack = useHasPriorInAppNavigation();

	useTrackView({ type: "view_item", sku: product.sku });

	// Media / video / use & care interactions all funnel into the one
	// pdp_content_click GA4 event; only the action + asset context vary.
	const trackContentClick = useCallback(
		(
			actionName: PdpContentClickAction,
			assetId?: string,
			position?: number
		) => {
			analyticsManager.track({
				type: "pdp_content_click",
				actionName,
				sku: product.sku,
				locale: locale as LocaleState,
				assetId,
				position
			});
		},
		[product.sku, locale]
	);

	const displayPrice = product.salePrice ?? 0;
	const listPrice = product.listPrice ?? 0;
	const imageUrl =
		product.heroImage?.url || product.lifestyleImage?.url || "";
	const useAndCareGuide = resolveUseAndCareGuide(product.contentBlocks);
	const useAndCareHref = buildUseAndCareHref(
		product.sku,
		product.slug,
		urls.useAndCareBase
	);

	const allBlocks = product.contentBlocks ?? [];
	const firstFeatureIdx = allBlocks.findIndex(
		(b) => b.blockType === "feature_highlight_block"
	);
	const baseFeatureBlock =
		firstFeatureIdx >= 0 ? allBlocks[firstFeatureIdx] : undefined;
	const featureBlock = baseFeatureBlock;
	const productTagItems = selectProductTags(product.productTags);
	const aboutProductBlock = allBlocks.find(
		(b) => b.blockType === "about_product"
	);
	// The section also carries the Use & Care affordance, the For Consultants
	// accordion and the Materials bullet, so it still has content to show for a
	// product with no authored `about_product` block.
	const aboutProductBlocks: CatalogContentBlock[] = [
		aboutProductBlock ?? { blockType: "about_product" }
	];
	// The store reports "anonymous" until UserManager hydrates it, so SSR
	// and first client render agree (no section) and consultants see it
	// appear post-hydration.
	const userType = useUserStore((s) => s.getUserType());
	const consultantSellingTips = resolveConsultantSellingTips(
		allBlocks,
		userType
	);
	const resolvedAboutProduct: AboutTheProductResolved = {
		specification: product.specification,
		consultantSellingTips,
		useAndCareGuide,
		useAndCareHref,
		onUseAndCareClick: () => trackContentClick("use_and_care_click")
	};
	const remainingBlocks = allBlocks
		.filter((_, i) => i !== firstFeatureIdx)
		.filter(
			(b) =>
				b.blockType !== "use_and_care_block" &&
				b.blockType !== "about_product" &&
				b.blockType !== "consultant_view_content" &&
				b.blockType !== "guarantees_block"
		);

	const { onClick, state } = useAddToCartSlider({
		sku: product.sku,
		quantity,
		product: {
			sku: product.sku,
			quantity,
			name: product.title,
			imageUrl,
			listPrice,
			salePrice: displayPrice
		}
	});

	const errorStatus = useAddToCartErrorStore((s) => s.status);
	const errorMessage = useAddToCartErrorStore((s) => s.message);
	const errorSeq = useAddToCartErrorStore((s) => s.seq);
	const clearError = useAddToCartErrorStore((s) => s.clear);

	const handleAddToCart = useCallback(() => {
		clearError();
		return onClick();
	}, [clearError, onClick]);

	useEffect(() => {
		clearError();
	}, [clearError]);

	// Scroll up so the error ribbon is visible even when add-to-cart was
	// triggered from the sticky bar or a cross-sell lower down the page.
	useEffect(() => {
		if (errorStatus) {
			window.scrollTo({ top: 0, behavior: "smooth" });
		}
	}, [errorStatus, errorSeq]);

	const stickyVisible = useStickyBarVisibility(
		'[data-component="ProductSummaryView"] [data-component="AddToCartButtonView"]'
	);
	// the bar grows to 96px at xl; below that an lg button crowds its 64px
	const stickyCtaIsLarge = useMediaQuery(
		`(min-width: ${breakpoint("xl")})`,
		true
	);

	return (
		<article
			data-template="ProductDetailTemplate"
			data-sku={product.sku}
			data-lifecycle={product.onlineStatus}
		>
			{errorStatus && errorMessage ? (
				<AlertBanner
					kind="error"
					content={errorMessage}
					cta={{ link: "", title: "" }}
					className={styles.atcErrorNotification}
				/>
			) : null}

			<Container>
				<Grid columns={12} gap="lg">
					<Col size={12}>
						<Breadcrumbs
							items={buildBreadcrumbItems(product, {
								homeUrl: urls.home,
								categoryUrlBase: urls.categoryBase
							})}
							showBack={showBack}
						/>
					</Col>

					<Col size={12}>
						<div className={styles["heroSection"]}>
							<div className={styles["galleryColumn"]}>
								<ProductGallery
									slides={toGallerySlides(product)}
									productTitle={product.title}
									onMediaClick={(slide, position) =>
										trackContentClick(
											"media_click",
											slide.assetId,
											position
										)
									}
									onVideoPlay={(slide, position) =>
										trackContentClick(
											"video_click",
											slide.assetId,
											position
										)
									}
								/>
							</div>
							<div className={styles["detailsColumn"]}>
								<div className={styles["productDetailsSticky"]}>
									<ProductSummary
										product={product}
										lowStockLabel={lowStockLabel}
										addToList={
											<AddToListRoot
												sku={product.sku}
												quantity={quantity}
												urls={urls}
											/>
										}
										quantity={quantity}
										onQuantityChange={setQuantity}
										addToCart={{
											onClick: handleAddToCart,
											loading: state.loading,
											error: null
										}}
										paymentMessage={
											typeof product.salePrice ===
											"number" ? (
												<PayPalMessage
													amount={product.salePrice}
													locale={locale}
												/>
											) : undefined
										}
									/>
								</div>
							</div>
						</div>
					</Col>
					{/* Stays two columns with no feature block, so About the
					    Product doesn’t stack full-width. */}
					<Col size={12}>
						<Grid
							columns={12}
							gap="xl"
							className={styles["featureGrid"]}
						>
							<Col size={12} lg={6}>
								{featureBlock ? (
									<ContentBlocksRenderer
										blocks={[featureBlock]}
										parentSku={product.sku}
									/>
								) : null}
								<ProductTagList
									items={productTagItems}
									className={styles["productTagRow"]}
								/>
							</Col>
							<Col size={12} lg={6}>
								<ContentBlocksRenderer
									blocks={aboutProductBlocks}
									parentSku={product.sku}
									resolvedAboutProduct={resolvedAboutProduct}
								/>
							</Col>
						</Grid>
					</Col>

					<Col size={12}>
						<ContentBlocksRenderer
							blocks={remainingBlocks}
							parentSku={product.sku}
							resolvedRecipePairings={resolvedRecipePairings}
							resolvedUpsellSets={resolvedUpsellSets}
						/>
					</Col>

					<Col size={12}>
						<RecommendedProductsBlock
							source="PDP"
							sku={product.sku}
							locale={locale}
						/>
					</Col>

					<Col size={12}>
						<ReviewsRatings
							sku={product.sku}
							locale={locale}
							{...config?.powerReviews}
						/>
					</Col>

					<Col size={12}>
						<ConsultantBanner
							captchaSiteKey={config?.captcha?.siteKey}
						/>
					</Col>

					<Col size={12}>
						<RecentlyViewedProducts
							currentSku={product.sku}
							embedded
							allowOverflow
						/>
					</Col>
				</Grid>
			</Container>

			<StickyCartBar
				visible={stickyVisible}
				product={product}
				addToCart={{ onClick: handleAddToCart, loading: state.loading }}
				ctaSize={stickyCtaIsLarge ? "lg" : "sm"}
			/>
		</article>
	);
}

function useHasPriorInAppNavigation(): boolean {
	const pathname = usePathname();
	const [hasPrior, setHasPrior] = useState(false);
	useEffect(() => {
		setHasPrior(readHasPriorInAppNav(pathname));
	}, [pathname]);
	return hasPrior;
}
